import { isUnofficialCountry } from '@travessa/shared';

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export interface MapLink {
  provider: 'google' | 'yandex';
  label: string;
  url: string;
}

const MAP_LABELS: Record<MapLink['provider'], string> = {
  google: 'Google Maps',
  yandex: 'Яндекс.Карты',
};

/**
 * External map links for a place. Uses coordinates when available (most precise)
 * and falls back to a name search. `providers` comes from the trip's preference,
 * else the traveller's map engine first (see `defaultMapProviders`); an old
 * `osm` in a saved preference is ignored.
 */
/** Links follow the map the traveller chose: theirs first, the other after. */
export function mapProvidersFor(
  tripPreference: string[] | undefined,
  engine: 'yandex' | 'google',
): string[] {
  const chosen = (tripPreference ?? []).filter((p) => p === 'google' || p === 'yandex');
  if (chosen.length) return chosen;
  return engine === 'google' ? ['google', 'yandex'] : ['yandex', 'google'];
}

export function buildMapLinks(
  name: string,
  lat: number | null,
  lng: number | null,
  providers: string[] | undefined,
): MapLink[] {
  const wanted = providers?.length ? providers : ['google', 'yandex'];
  const q = encodeURIComponent(name);
  const hasCoords = lat !== null && lng !== null;
  const out: MapLink[] = [];
  for (const p of wanted) {
    if (p === 'google') {
      out.push({
        provider: 'google',
        label: MAP_LABELS.google,
        // Name *and* coordinates. Coordinates alone drop a pin in the middle of
        // nowhere in particular and show no place card; the name is what makes
        // the result the restaurant rather than the pavement outside it.
        url: hasCoords
          ? `https://www.google.com/maps/search/?api=1&query=${q}%20${lat},${lng}`
          : `https://www.google.com/maps/search/?api=1&query=${q}`,
      });
    } else if (p === 'yandex') {
      out.push({
        provider: 'yandex',
        label: MAP_LABELS.yandex,
        // A search for the name, centred on the point.
        //
        // `?pt=` alone — which this used to build — simply does not open: Yandex
        // needs `ll` to know where to centre, and without it the map comes up
        // blank. Searching by name near the coordinates lands on the
        // organisation's own card (/maps/org/...), which is what someone
        // clicking a restaurant actually wants: hours, phone, reviews, and
        // whether it is still open.
        url: hasCoords
          ? `https://yandex.ru/maps/?mode=search&text=${q}&sll=${lng},${lat}&ll=${lng},${lat}&z=17`
          : `https://yandex.ru/maps/?mode=search&text=${q}`,
      });
    }
  }
  return out;
}

/**
 * «Маршрут» to a place in the traveller's preferred map app: on foot when it
 * is a walk away, by car otherwise. Without the traveller's position the app
 * routes from wherever the phone says it is. Null when the place has no
 * coordinates — a route to a name search lands wherever the app guesses.
 */
export function buildDirectionsLink(
  to: { lat: number | null; lng: number | null },
  from: { lat: number; lng: number } | null,
  distanceM: number | null,
  providers: string[] | undefined,
): { label: string; url: string } | null {
  if (to.lat === null || to.lng === null) return null;
  const walking = distanceM === null || distanceM <= 3_000;
  const provider = (providers?.length ? providers : ['google', 'yandex']).find(
    (p) => p === 'google' || p === 'yandex',
  );
  if (provider === 'yandex') {
    const origin = from ? `${from.lat},${from.lng}` : '';
    return {
      label: MAP_LABELS.yandex,
      url: `https://yandex.ru/maps/?rtext=${origin}~${to.lat},${to.lng}&rtt=${walking ? 'pd' : 'auto'}`,
    };
  }
  const origin = from ? `&origin=${from.lat},${from.lng}` : '';
  return {
    label: MAP_LABELS.google,
    url: `https://www.google.com/maps/dir/?api=1${origin}&destination=${to.lat},${to.lng}&travelmode=${walking ? 'walking' : 'driving'}`,
  };
}

export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export const nightsLabel = (n: number) => `${n} ${plural(n, 'ночь', 'ночи', 'ночей')}`;
export const daysLabel = (n: number) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;
export const placesLabel = (n: number) => `${n} ${plural(n, 'место', 'места', 'мест')}`;
export const photosLabel = (n: number) => `${n} ${plural(n, 'фото', 'фото', 'фото')}`;

export function formatDate(iso: string | null | undefined, withWeekday = false): string {
  if (!iso) return '';
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  const base = `${date.getUTCDate()} ${MONTHS_GEN[date.getUTCMonth()] ?? ''}`;
  return withWeekday ? `${base} · ${WEEKDAYS[date.getUTCDay()] ?? ''}` : base;
}

export function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return '';
  const from = new Date(`${start.slice(0, 10)}T00:00:00Z`);
  if (!end || end.slice(0, 10) === start.slice(0, 10)) return `${from.getUTCDate()} ${MONTHS_GEN[from.getUTCMonth()] ?? ''} ${from.getUTCFullYear()}`;
  const to = new Date(`${end.slice(0, 10)}T00:00:00Z`);
  const sameMonth = from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
  return sameMonth
    ? `${from.getUTCDate()}–${to.getUTCDate()} ${MONTHS_GEN[to.getUTCMonth()] ?? ''} ${to.getUTCFullYear()}`
    : `${from.getUTCDate()} ${MONTHS_GEN[from.getUTCMonth()] ?? ''} – ${to.getUTCDate()} ${MONTHS_GEN[to.getUTCMonth()] ?? ''} ${to.getUTCFullYear()}`;
}

export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const units = ['КБ', 'МБ', 'ГБ', 'ТБ'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Flag emoji from an ISO-3166-1 alpha-2 code, computed rather than looked up.
 *
 * Each letter maps to a Unicode regional indicator symbol (🇦 = U+1F1E6 + 0,
 * 🇧 = U+1F1E6 + 1, …); a country code is just two of them in a row. This used
 * to be a hand-picked table of ~20 codes the trip form happened to need, which
 * meant every new destination or citizenship needed its own entry. The
 * computed version covers all ~249 codes for free.
 */
export const countryFlag = (code: string): string => {
  const upper = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return '🌍';
  // Not ISO (Abkhazia's `AB`): no emoji, and two bare letters would read as a typo.
  if (isUnofficialCountry(upper)) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(...[...upper].map((letter) => base + letter.charCodeAt(0) - 65));
};

export const STYLE_LABELS: Record<string, string> = {
  nature: 'Природа',
  culture: 'Культура',
  beach: 'Пляж',
  food: 'Гастрономия',
  nightlife: 'Ночная жизнь',
  active: 'Актив',
  roadtrip: 'Автопутешествие',
  family: 'С детьми',
  romantic: 'Романтика',
  budget: 'Бюджетно',
  luxury: 'Премиум',
};

export const PACE_LABELS: Record<string, string> = {
  relaxed: 'Расслабленный',
  balanced: 'Сбалансированный',
  packed: 'Насыщенный',
};

export const PLACE_KIND_LABELS: Record<string, string> = {
  sight: 'Достопримечательность',
  ruins: 'Руины',
  nature: 'Природа',
  beach: 'Пляж',
  viewpoint: 'Смотровая',
  city: 'Город',
  lodging: 'Жильё',
  food: 'Еда',
  activity: 'Активность',
  transport: 'Транспорт',
  airport: 'Аэропорт',
  shopping: 'Шопинг',
  service: 'Сервис',
  other: 'Другое',
};

export const PLACE_KIND_ICONS: Record<string, string> = {
  sight: '📍', ruins: '🏛️', nature: '🌿', beach: '🏖️', viewpoint: '🌄', city: '🏙️',
  lodging: '🛏️', food: '🍽️', activity: '🛶', transport: '🚗', airport: '✈️',
  shopping: '🛍️', service: 'ℹ️', other: '📌',
};

export const ACTIVITY_KIND_ICONS: Record<string, string> = {
  note: '📝', visit: '📍', meal: '🍽️', transfer: '🚕', checkin: '🔑', checkout: '🚪',
  flight: '✈️', drive: '🚗', walk: '🥾', boat: '⛵', tour: '🎟️', rest: '☕',
  shopping: '🛍️', other: '•',
};

export const RESERVATION_KIND_LABELS: Record<string, string> = {
  flight: 'Перелёт',
  lodging: 'Проживание',
  car: 'Аренда авто',
  train: 'Поезд',
  bus: 'Автобус',
  ferry: 'Паром',
  tour: 'Экскурсия',
  insurance: 'Страховка',
  other: 'Прочее',
};

/** Common currencies across the trip form, the budget field and manual bookings. */
export const CURRENCIES = ['RUB', 'USD', 'EUR', 'TRY', 'GEL', 'AED', 'THB', 'RSD'];

export const DOC_GROUP_LABELS: Record<string, string> = {
  tickets: 'Билеты',
  lodging: 'Отели',
  car: 'Аренда авто',
  insurance: 'Страховка',
  notes: 'Заметки',
  other: 'Прочее',
};

export function initials(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return `${(parts[0] as string)[0] ?? ''}${(parts[1] as string)[0] ?? ''}`.toUpperCase();
}

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('ru-RU')} ${currency}`;
  }
}

/**
 * A place's price as a traveller reads it: per person, and for the whole
 * party beside it — «500 ₽ на человека · 1 000 ₽ за 2». An estimate says so
 * with «≈»; a free one says «бесплатно». Null when there is no price.
 */
export function priceLabel(
  cost: { amount: number; currency: string } | null | undefined,
  ticket: { status: string } | null | undefined,
  party: number,
): string | null {
  if (ticket?.status === 'free' || (cost && cost.amount === 0 && ticket?.status !== 'estimated')) {
    return cost || ticket?.status === 'free' ? 'бесплатно' : null;
  }
  if (!cost || !(cost.amount > 0)) return null;
  const about = ticket?.status === 'estimated' ? '≈ ' : '';
  const one = `${about}${money(cost.amount, cost.currency)} на человека`;
  return party > 1 ? `${one} · ${about}${money(cost.amount * party, cost.currency)} за ${party}` : one;
}
